import React, { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { ButtonStandard, FormHandle, FormInputText, ModalBase, PYForm, Text } from "@performyard/py-components";
import type { CreateArtifactRequest, UpdateArtifactRequest } from "@performyard/service-contracts";

import type { ChatOutput } from "../types";

import { useReportingChatStore } from "../store";

import { useArtifacts, useCreateArtifact, useUpdateArtifact } from "../../../../../api/hooks";

import { buildUniqueArtifactName } from "../utils/buildUniqueArtifactName";

const saveArtifactSchema = z.object({
	name: z.string().trim().min(1, "Name is required"),
});
type SaveArtifactFormValues = z.infer<typeof saveArtifactSchema>;

// Builds the create/update body from an attached output. Answers save a text snapshot with no
// query; table/chart flatten the frame's nested {reportType, params, output} into the artifact's
// flat query shape. The casts bridge the agent's permissive param shape to the stricter contract
// types — validated structurally at the internal-api boundary, not by the frontend compiler.
// Chat history is not sent — saving it was deferred post-prototype.
const buildArtifactBody = (output: ChatOutput, name: string) => {
	if (output.kind === "answer") {
		return {
			name,
			outputType: "answer" as const,
			output: { title: name, answerText: output.answer, underlyingQuery: output.underlyingQuery },
		};
	}
	return {
		name,
		outputType: output.kind,
		query: output.query
			? ({ reportType: output.query.reportType, ...output.query.params } as CreateArtifactRequest["query"])
			: undefined,
		output: (output.query?.output ?? { title: output.title }) as CreateArtifactRequest["output"],
	};
};

export const SaveArtifactModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	output: ChatOutput;
	defaultName: string;
}> = ({ isOpen, onClose, output, defaultName }) => {
	const loadedArtifactId = useReportingChatStore((state) => state.loadedArtifactId);
	const formRef = useRef<FormHandle<typeof saveArtifactSchema>>(null);
	const [apiError, setApiError] = useState<string | null>(null);
	const createArtifact = useCreateArtifact();
	const updateArtifact = useUpdateArtifact();
	const artifactsQuery = useArtifacts();
	const isSaving = createArtifact.isPending || updateArtifact.isPending;

	useEffect(() => {
		if (isOpen) setApiError(null);
	}, [isOpen]);

	// The modal mounts closed alongside the output bubble, so the list is already settled (and
	// possibly stale) well before the user opens it — refetching on open hands the fresh/error
	// handling to the gate below (isNameResolved / hasStaleNameError).
	useEffect(() => {
		if (isOpen && loadedArtifactId) artifactsQuery.refetch();
	}, [isOpen, loadedArtifactId]);

	// In update mode the artifact's OWN stored name is the correct default — never the output
	// title, which is a different field entirely and would silently rename the artifact if the
	// user submits without editing. undefined while artifactsQuery hasn't returned data yet.
	const currentArtifactName = useMemo(() => {
		if (!loadedArtifactId) return undefined;
		return artifactsQuery.data?.artifacts.find((artifact) => artifact._id === loadedArtifactId)?.name;
	}, [artifactsQuery.data, loadedArtifactId]);

	// Once the artifacts list has returned (even if this id somehow isn't in it), we're done
	// waiting — the field is either pre-filled with the real name or left blank for the user to
	// fill in themselves. It's only "pending" while the GET is still in flight.
	const isNameResolved = !loadedArtifactId || (artifactsQuery.data !== undefined && !artifactsQuery.isFetching);

	// A resolved gate only means the GET settled — not that its data is trustworthy. A failed
	// background refetch leaves react-query's `data` at its last-good (stale) value with
	// isFetching:false, so isNameResolved alone would silently seed the old name and let an
	// untouched Update PUT it right back. This derives straight from isError every render (no
	// memo), so it clears the instant a later refetch succeeds — nothing to go stale.
	const hasStaleNameError = !!loadedArtifactId && isNameResolved && artifactsQuery.isError;

	// buildUniqueArtifactName's collision-avoiding suffix is only wanted for create — updating an
	// artifact under its own existing name isn't a collision. Blank while !isNameResolved (not just
	// while currentArtifactName is undefined) so a warm-stale revalidation doesn't pre-fill the
	// disabled field with the old name — it stays blank until the fresh name lands. Also blank on
	// hasStaleNameError — seeding stale data here would silently resurrect the exact bug this guards
	// against once the field is enabled.
	const suggestedName = useMemo(() => {
		if (loadedArtifactId) return isNameResolved && !hasStaleNameError ? (currentArtifactName ?? "") : "";
		const otherNames = (artifactsQuery.data?.artifacts ?? []).map((artifact) => artifact.name);
		return buildUniqueArtifactName(defaultName, otherNames);
	}, [defaultName, artifactsQuery.data, loadedArtifactId, currentArtifactName, isNameResolved, hasStaleNameError]);

	const handleSubmit = async ({ value }: { value: SaveArtifactFormValues }) => {
		setApiError(null);
		const body = buildArtifactBody(output, value.name.trim());

		try {
			if (loadedArtifactId) {
				await updateArtifact.mutateAsync({ id: loadedArtifactId, body: body as UpdateArtifactRequest });
			} else {
				await createArtifact.mutateAsync(body as CreateArtifactRequest);
			}
			onClose();
		} catch (error: any) {
			setApiError(error?.data?.message || "Could not save this report.");
		}
	};

	return (
		<ModalBase isOpen={isOpen} onClose={onClose}>
			<div className="w-[400px] px-6 pb-6">
				<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="mb-4 text-center">
					{loadedArtifactId ? "Update Report" : "Save Report"}
				</Text>
				<PYForm
					// PYForm's underlying TanStack form only reads defaultValues at construction, so
					// changing suggestedName after mount (e.g. artifactsQuery resolving late) would
					// silently leave a stale value in place. Fold the resolved/pending/error transition
					// into the key so each one forces a remount; create mode's key stays stable for the
					// whole open session so it doesn't clobber in-progress typing. The gate now also
					// waits out a background revalidation, so a rename in another tab can't silently get
					// PUT back — this key still forces the remount onto the fresh name once it lands.
					// "error" is its own segment (not folded into "resolved") specifically so a later
					// successful refetch — which lands back on "resolved" — still changes the key and
					// remounts onto the fresh name, instead of silently leaving the blank error-state
					// value in place.
					key={
						!isOpen
							? "closed"
							: loadedArtifactId
								? `open-update-${loadedArtifactId}-${
										!isNameResolved ? "pending" : hasStaleNameError ? "error" : "resolved"
									}`
								: "open-create"
					}
					ref={formRef}
					id="save-artifact-form"
					schema={saveArtifactSchema}
					defaultValues={{ name: suggestedName }}
					onSubmit={handleSubmit}
					validateOn={["onSubmit"]}
				>
					<div className="flex flex-col gap-4">
						<FormInputText
							id="name"
							label="Report Name"
							placeholder={
								hasStaleNameError
									? "Enter report name"
									: isNameResolved
										? "Untitled report"
										: "Loading current name…"
							}
							disabled={!isNameResolved}
						/>
						{hasStaleNameError && (
							<Text size="caption" color="red-5">
								Couldn&apos;t confirm this report&apos;s current name — enter it manually to update, or
								cancel.
							</Text>
						)}
						{apiError && (
							<Text size="caption" color="red-5">
								{apiError}
							</Text>
						)}
						<div className="mt-2 flex justify-center gap-2">
							<ButtonStandard variant="outline" color="neutral" size="large" onClick={onClose}>
								Cancel
							</ButtonStandard>
							<ButtonStandard
								variant="solid"
								color="primary"
								size="large"
								type="button"
								disabled={isSaving || !isNameResolved}
								onClick={async () => {
									const isValid = await formRef.current?.validate(true);
									if (isValid) await formRef.current?.submit();
								}}
							>
								{isSaving ? "Saving…" : loadedArtifactId ? "Update" : "Save"}
							</ButtonStandard>
						</div>
					</div>
				</PYForm>
			</div>
		</ModalBase>
	);
};

export default SaveArtifactModal;
